import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Habit, CheckInLog } from '../types';
import { format, isWithinInterval, startOfWeek, endOfWeek, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { Quote, Image as ImageIcon, X, Calendar, Edit2, Trash2, Filter } from 'lucide-react';
import { CheckInImage } from './CheckInImage';

interface DiaryViewProps {
    habits: Habit[];
    onDeleteLog: (habitId: string, log: CheckInLog) => void;
    onEditLog: (habit: Habit, log: CheckInLog) => void;
    memberSwitcher?: React.ReactNode;
}

type FilterType = 'all' | 'week' | 'month';

export const DiaryView: React.FC<DiaryViewProps> = ({ habits, onDeleteLog, onEditLog, memberSwitcher }) => {
    const [filterType, setFilterType] = useState<FilterType>('week');
    const [visibleImagePaths, setVisibleImagePaths] = useState<Set<string>>(new Set());
    const observerRef = useRef<IntersectionObserver | null>(null);
    const imageRefs = useRef<Map<string, HTMLDivElement>>(new Map());

    // Get all logs from all habits and apply filters
    const filteredLogs = useMemo(() => {
        let logs: { habit: Habit; log: CheckInLog }[] = [];

        habits.forEach(h => {
            if (h.logs && h.logs.length > 0) {
                h.logs.forEach(l => {
                    logs.push({ habit: h, log: l });
                });
            }
        });

        const now = new Date();
        const weekInterval = { start: startOfWeek(now), end: endOfWeek(now) };
        const monthInterval = { start: startOfMonth(now), end: endOfMonth(now) };

        const filtered = logs.filter(({ log }) => {
            if (filterType === 'all') return true;
            const logDate = parseISO(log.date);
            if (filterType === 'week') {
                return isWithinInterval(logDate, weekInterval);
            }
            if (filterType === 'month') {
                return isWithinInterval(logDate, monthInterval);
            }
            return true;
        });

        return filtered.sort((a, b) => {
            if (a.log.date !== b.log.date) return b.log.date.localeCompare(a.log.date);
            return b.log.timestamp - a.log.timestamp;
        });
    }, [habits, filterType]);

    // Setup Intersection Observer for lazy loading
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const imagePath = entry.target.getAttribute('data-image-path');
                        if (imagePath && !visibleImagePaths.has(imagePath)) {
                            setVisibleImagePaths(prev => new Set([...prev, imagePath]));
                        }
                    }
                });
            },
            {
                rootMargin: '300px 0px',
                threshold: 0.01
            }
        );

        observerRef.current = observer;

        // Observe all image containers
        imageRefs.current.forEach((element) => {
            observer.observe(element);
        });

        return () => {
            observer.disconnect();
        };
    }, [visibleImagePaths]);

    // Update image refs when logs change
    useEffect(() => {
        imageRefs.current.clear();
    }, [filteredLogs]);

    // Batch preload images for visible ones
    useEffect(() => {
        const pathsToPreload = Array.from(visibleImagePaths)
            .filter((path): path is string => typeof path === 'string' && path && !path.startsWith('data:') && !path.startsWith('http'));

        if (pathsToPreload.length > 0) {
            import('../services/supabaseClient').then(m => {
                m.getCheckInImageUrls(pathsToPreload);
            });
        }
    }, [visibleImagePaths]);

    const registerImageRef = (imagePath: string) => (element: HTMLDivElement | null) => {
        if (element) {
            imageRefs.current.set(imagePath, element);
            if (observerRef.current) {
                observerRef.current.observe(element);
            }
        } else {
            imageRefs.current.delete(imagePath);
        }
    };

    return (
        <div className="pb-8 animate-fade-in">
            {/* Fixed Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-slate-900 -mx-6 px-6 pt-6 pb-4">
                {memberSwitcher}
                <div className="flex flex-col gap-4 mt-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                            <span className="p-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg">
                                <Calendar size={20} />
                            </span>
                            打卡日记
                        </h2>
                        <span className="text-xs font-normal text-slate-400 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full">
                            共 {filteredLogs.length} 条
                        </span>
                    </div>

                    {/* Filter Tabs */}
                    <div className="flex bg-slate-100 dark:bg-slate-800/50 p-1 rounded-xl w-fit">
                        {(['all', 'week', 'month'] as const).map((type) => (
                            <button
                                key={type}
                                onClick={() => setFilterType(type)}
                                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${filterType === type
                                    ? 'bg-white dark:bg-slate-700 text-primary-600 dark:text-primary-400 shadow-sm'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                                    }`}
                            >
                                {type === 'all' ? '全部' : type === 'week' ? '本周' : '本月'}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Scrollable Content */}
            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-2.5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent dark:before:via-slate-700 mt-4">
                {filteredLogs.length > 0 ? (
                    filteredLogs.map(({ habit, log }) => (
                        <div key={log.id} className="relative flex items-start gap-4 group">
                            <div className="absolute left-0 mt-1.5 ml-1.5 w-3 h-3 rounded-full border-2 border-white dark:border-slate-800 bg-slate-300 dark:bg-slate-600 group-hover:bg-primary-500 transition-colors shadow-sm"></div>
                            <div className="flex-1 ml-6">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2">
                                    <div className="flex items-center gap-2 mb-1 sm:mb-0">
                                        <span className={`w-2 h-2 rounded-full ${habit.color}`}></span>
                                        <span className="font-bold text-slate-800 dark:text-white text-sm">{habit.title}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-slate-400 font-mono">{format(new Date(log.date), 'yyyy年MM月dd日')}</span>

                                        {/* Action Buttons */}
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => onEditLog(habit, log)}
                                                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md text-slate-400 hover:text-primary-500"
                                                title="编辑"
                                            >
                                                <Edit2 size={14} />
                                            </button>
                                            <button
                                                onClick={() => onDeleteLog(habit.id, log)}
                                                className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md text-slate-400 hover:text-red-500"
                                                title="删除"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-white dark:bg-slate-800 rounded-xl p-4 transition-all hover:shadow-md border border-slate-100 dark:border-slate-700">
                                    {log.note && (
                                        <div className="flex gap-2 mb-3">
                                            <Quote size={16} className="text-slate-300 flex-shrink-0 mt-1" />
                                            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">{log.note}</p>
                                        </div>
                                    )}
                                    {log.image && (
                                        <div ref={registerImageRef(log.image)} data-image-path={log.image}>
                                            <CheckInImage imagePath={log.image} />
                                        </div>
                                    )}
                                    {!log.note && !log.image && (
                                        <p className="text-xs text-slate-400 italic">完成打卡 (无附加记录)</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center py-12 pl-6">
                        <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                            <Calendar size={32} />
                        </div>
                        <p className="text-slate-400 text-sm">
                            {filterType === 'all' ? '还没有打卡记录哦' : `本${filterType === 'week' ? '周' : '月'}还没有打卡记录哦`}
                        </p>
                        <p className="text-xs text-slate-300 mt-2">
                            坚持打卡并记录心得，这里会越来越丰富
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

